import axios from "axios";
import { authenticationService } from "../jwt/_services/authentication.service";
import { parseJwt } from "../jwt/_helpers/auth-header";
import { isDemoMode, isBrowserSessionDemo } from "./demoConfig";
import { getMergedDemoUser, persistRealUserIdentity } from "./demoTwoFactorHelpers";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

function buildUserRequestOptions(token) {
  return {
    method: "GET",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json",
    },
  };
}

export function extractUserFromApiResponse(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  if (Array.isArray(payload.results) && payload.results.length > 0) {
    return payload.results[0];
  }
  if (Array.isArray(payload) && payload.length > 0) {
    return payload[0];
  }

  const hasIdentity =
    payload.email ||
    payload.username ||
    payload.first_name ||
    payload.firstname ||
    payload.last_name ||
    payload.lastname;

  if (!hasIdentity) {
    return null;
  }

  // Django-style paginated user, FastAPI profile object, or login payload
  if (payload.id != null || payload.userid != null || payload.email || payload.username) {
    return payload;
  }

  return null;
}

/** Map FastAPI fields (firstname/lastname/userid) onto UI fields (first_name/last_name/id). */
export function normalizeRealUserIdentity(realUser) {
  if (!realUser || typeof realUser !== "object") {
    return null;
  }

  const firstname = String(realUser.firstname || realUser.first_name || "").trim();
  const lastname = String(realUser.lastname || realUser.last_name || "").trim();
  const email = String(realUser.email || "").trim();
  const username = String(realUser.username || realUser.email || "").trim();

  return {
    ...realUser,
    id: realUser.id ?? realUser.userid,
    firstname,
    lastname,
    first_name: String(realUser.first_name || firstname).trim(),
    last_name: String(realUser.last_name || lastname).trim(),
    email,
    username,
  };
}

function extractIdentityFromJwt(token) {
  if (!token) {
    return null;
  }
  try {
    const payload = parseJwt(token);
    const firstName = payload.first_name || payload.firstname || payload.given_name || "";
    const lastName = payload.last_name || payload.lastname || payload.family_name || "";
    const email = payload.email || payload.username || "";
    if (!firstName && !lastName && !email) {
      return null;
    }
    return {
      first_name: firstName,
      last_name: lastName,
      firstname: payload.firstname || payload.first_name || payload.given_name || firstName,
      email,
      username: payload.username || payload.email || email,
    };
  } catch {
    return null;
  }
}

async function fetchRealUserIdentity(token) {
  if (!token) {
    return null;
  }

  const requestConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await axios.get(`${API_URL}users/`, requestConfig);
    const realUser = extractUserFromApiResponse(response.data);
    if (realUser) {
      return realUser;
    }
  } catch (error) {
    console.log("axios users/ identity fetch failed", error);
  }

  try {
    const response = await fetch(`${API_URL}users/`, buildUserRequestOptions(token));
    if (response.status === 401) {
      authenticationService.logout();
      return null;
    }
    if (!response.ok) {
      return null;
    }
    const json = await response.json();
    return extractUserFromApiResponse(json);
  } catch (error) {
    console.log("fetch users/ identity fetch failed", error);
    return null;
  }
}

/** Fetch FastAPI user identity, persist it, and return the parsed user (or null). */
export async function refreshRealUserIdentity(token) {
  const realUser = await fetchRealUserIdentity(token);
  if (realUser) {
    const normalized = normalizeRealUserIdentity(realUser);
    persistRealUserIdentity(normalized || realUser);
    return normalized || realUser;
  }

  const jwtIdentity = extractIdentityFromJwt(token);
  if (jwtIdentity) {
    persistRealUserIdentity(jwtIdentity);
    return jwtIdentity;
  }

  return null;
}

/** Overlay real FastAPI identity onto demo user; keep demo permissions and profile fields. */
export function mergeDemoUserWithRealIdentity(demoUser, realUser) {
  if (!realUser || typeof realUser !== "object") {
    return demoUser;
  }

  const normalized = normalizeRealUserIdentity(realUser);
  if (!normalized) {
    return demoUser;
  }

  return {
    ...demoUser,
    first_name: normalized.first_name || demoUser.first_name,
    last_name: normalized.last_name || demoUser.last_name,
    firstname: normalized.firstname || normalized.first_name || demoUser.firstname,
    lastname: normalized.lastname || normalized.last_name || demoUser.lastname,
    email: normalized.email || demoUser.email,
    username: normalized.username || demoUser.username,
  };
}

export async function resolveDemoUserWithRealIdentity(token, demoUser) {
  const realUser = await refreshRealUserIdentity(token);
  return mergeDemoUserWithRealIdentity(demoUser, realUser);
}

const getUser = async(token, currentUser, setCurrentUser) => {
  if (!token) return false;

  if (isDemoMode() || isBrowserSessionDemo()) {
    const demoUser = getMergedDemoUser();
    const mergedUser = await resolveDemoUserWithRealIdentity(token, demoUser);
    setCurrentUser(mergedUser);
    return true;
  }

  try {
    const response = await fetch(`${API_URL}users/`, buildUserRequestOptions(token));
    if (response.status === 401) {
      authenticationService.logout();
      return false;
    }
    const json = await response.json();

    setCurrentUser(json.results[0]);
    
  } catch (error) {
    console.log('error', error);
  }
}
export { getUser };
